/**
 * Article #9 — Best Pool Coping Materials for South Florida.
 *
 * Shared article data: TOC entries (matching the prepared H2 sections), the
 * FAQ array, and the empty image URL/alt/caption constants for the featured
 * and supporting images.
 *
 * Both the visible FAQ section and the FAQPage JSON-LD schema consume this
 * same FAQ array.
 *
 * Every image URL below is intentionally empty until an approved authentic
 * Precision project image is supplied. The image components render nothing
 * while a URL is empty, so no broken image elements appear.
 */

export interface Faq {
  question: string;
  answer: string;
}

export const FAQS_POOL_COPING_MATERIALS: Faq[] = [
  {
    question: "What is the best pool coping material for South Florida?",
    answer:
      "There is no single material that is best for every South Florida pool. The right choice depends on the exact product, surface finish, edge profile, sun exposure, wet-area use, pool geometry, surrounding deck, maintenance expectations, design goals, and installation conditions.",
  },
  {
    question: "Is travertine or marble better for pool coping?",
    answer:
      "Neither category is automatically better. Travertine and marble differ by specific stone, color, finish, density, variation, fabrication, maintenance guidance, and supplier. Compare the exact products in person and evaluate how each works with the tile, finish, deck, and property conditions.",
  },
  {
    question: "Does pool coping have to match the pool deck?",
    answer:
      "No. Coping may match the deck, coordinate with a related color, or create an intentional contrast. The important issues are visual compatibility, material transitions, dimensions, joints, elevations, maintenance, and installation details.",
  },
  {
    question: "What is the difference between pool coping and waterline tile?",
    answer:
      "Coping caps the upper perimeter edge of the pool. Waterline tile is installed vertically near the water surface below the coping. They meet visually and physically, but they are different components with different roles.",
  },
  {
    question: "Is bullnose coping safer than square-edge coping?",
    answer:
      "A rounded bullnose may provide a softer edge for hands and feet, but safety cannot be determined from the profile name alone. Surface texture, fabrication, installation, wet conditions, maintenance, elevation changes, and the exact product also matter.",
  },
  {
    question: "Does pool coping need to be replaced during resurfacing?",
    answer:
      "Not automatically. Existing coping may remain when it is sound, compatible with the new finish and tile, and suitable for the project sequence. Loose, damaged, moving, incompatible, or poorly coordinated coping may justify repair, replacement, or further evaluation.",
  },
  {
    question: "Should pool coping be sealed?",
    answer:
      "Sealing depends on the exact material, surface finish, exposure, manufacturer or supplier guidance, and selected sealer. Do not assume every coping product requires the same treatment. Confirm the recommendation for the specific installed product.",
  },
  {
    question: "Can I choose pool coping from photographs online?",
    answer:
      "Photographs are useful for inspiration, but they do not reliably show texture, heat, edge comfort, thickness, natural variation, or true color. Review physical samples and the exact product information before approving the final selection.",
  },
];

export const TOC_POOL_COPING_MATERIALS = [
  { id: "the-short-answer", title: "The Short Answer" },
  {
    id: "what-pool-coping-is",
    title: "What Pool Coping Is—and What It Is Not",
  },
  {
    id: "pool-coping-materials-at-a-glance",
    title: "Pool Coping Materials at a Glance",
  },
  { id: "concrete-and-paver-coping", title: "Concrete and Paver Coping" },
  { id: "travertine-pool-coping", title: "Travertine Pool Coping" },
  { id: "marble-pool-coping", title: "Marble Pool Coping" },
  { id: "porcelain-pool-coping", title: "Porcelain Pool Coping" },
  {
    id: "other-pool-coping-options",
    title: "Other Natural-Stone and Coping Options",
  },
  {
    id: "pool-coping-edge-profiles",
    title: "Pool Coping Edge Profiles and Styles",
  },
  {
    id: "south-florida-coping-considerations",
    title: "How South Florida Conditions Affect Coping Selection",
  },
  {
    id: "coordinate-coping-tile-finish-decking",
    title: "How to Coordinate Coping With Tile, Finish, and Decking",
  },
  {
    id: "keep-repair-or-replace-pool-coping",
    title: "Should Existing Pool Coping Be Kept, Repaired, or Replaced?",
  },
  {
    id: "pool-coping-selection-checklist",
    title: "What to Compare Before Choosing Pool Coping",
  },
  { id: "frequently-asked-questions", title: "Frequently Asked Questions" },
];

// --- Image constants ---

// Featured image — authentic completed Precision pool project clearly
// showing coping, waterline tile, pool finish, and surrounding deck.
export const FEATURED_IMAGE_POOL_COPING_MATERIALS =
  "https://vibe.filesafe.space/1790702435770123164/attachments/251de517-9afd-4516-9d06-cdcd5eebbf53.jpg";
export const FEATURED_IMAGE_POOL_COPING_MATERIALS_ALT =
  "Completed Precision pool project showing the coping edge, raised spa, waterline tile, and surrounding deck";
export const FEATURED_IMAGE_POOL_COPING_MATERIALS_CAPTION =
  "An authentic Precision Pavers & Pool Design project showing how the coping edge coordinates with the raised spa, waterline tile, and surrounding deck.";

// Supporting image #1 — Capri-Limestone-Paver-Deck-1.jpg
export const SUPPORTING_IMAGE_POOL_COPING_MATERIALS_EDGE =
  "https://vibe.filesafe.space/1790702435770123164/attachments/4067709a-ab27-40c3-8a74-de70288188db.jpg";
export const SUPPORTING_IMAGE_POOL_COPING_MATERIALS_EDGE_ALT =
  "Completed Precision pool project showing a defined pool edge coordinating with the surrounding deck and raised spa";
export const SUPPORTING_IMAGE_POOL_COPING_MATERIALS_EDGE_CAPTION =
  "An authentic Precision Pavers & Pool Design project showing how the pool edge, raised spa, waterline tile, and deck can be coordinated as one visual system.";

// Supporting image #2 — Pool Deck Pavers Resurfacing in Broward County Florida6.jpg
export const SUPPORTING_IMAGE_POOL_COPING_MATERIALS_COORDINATED =
  "https://vibe.filesafe.space/1790702435770123164/attachments/0f7b2a22-a36b-4ec6-af78-e46ab6f48830.jpg";
export const SUPPORTING_IMAGE_POOL_COPING_MATERIALS_COORDINATED_ALT =
  "Completed Precision pool project showing the relationship between the pool edge, waterline tile, raised feature, and surrounding deck";
export const SUPPORTING_IMAGE_POOL_COPING_MATERIALS_COORDINATED_CAPTION =
  "The coping decision should be evaluated alongside the waterline tile, raised features, pool shape, and surrounding deck—not as an isolated material choice.";
